import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box, Typography, Paper, Button, Alert, CircularProgress, LinearProgress,
  Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, IconButton, Collapse, Dialog, DialogTitle, DialogContent,
  DialogActions, Card, CardContent, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import DownloadIcon from '@mui/icons-material/Download';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CodeIcon from '@mui/icons-material/Code';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { authGet, authPost, authDelete } from '../../services/apiService';

const darkGreen = '#1D4434';

// ─── helpers ─────────────────────────────────────────────────────────────────

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const fmtDuration = (startIso, endIso) => {
  if (!startIso || !endIso) return '—';
  const ms = new Date(endIso) - new Date(startIso);
  if (ms < 0) return '—';
  if (ms < 60000) return `${Math.round(ms / 1000)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
};

const esc = (v) => String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const STATUS_BADGE_STYLE = {
  success:   'background:#4caf50;color:#fff',
  completed: 'background:#4caf50;color:#fff',
  error:     'background:#f44336;color:#fff',
  failed:    'background:#f44336;color:#fff',
  timeout:   'background:#ff9800;color:#fff',
  running:   'background:#1976d2;color:#fff',
};

const badge = (status) => {
  const s = (status || '').toLowerCase();
  const style = STATUS_BADGE_STYLE[s] || 'background:#607d8b;color:#fff';
  return `<span style="display:inline-block;padding:2px 10px;border-radius:12px;font-size:0.78rem;font-weight:600;${style}">${esc(status || '—')}</span>`;
};

const exportRunsHtml = (runs, metrics, filterLabel) => {
  const date = new Date().toISOString().slice(0, 10);
  const completed = runs.filter(r => r.status === 'completed');
  const totalTests = completed.reduce((s, r) => s + (r.summary?.total ?? 0), 0);
  const totalSuccess = completed.reduce((s, r) => s + (r.summary?.success ?? 0), 0);
  const totalErrors = completed.reduce((s, r) => s + (r.summary?.error ?? 0), 0);
  const totalTimeouts = completed.reduce((s, r) => s + (r.summary?.timeout ?? 0), 0);

  const summaryCards = [
    { label: 'Total Runs', value: runs.length, color: '#1D4434' },
    { label: 'Success Rate', value: metrics?.successRate != null ? `${metrics.successRate}%` : '—', color: metrics?.successRate >= 90 ? '#4caf50' : metrics?.successRate >= 70 ? '#f57c00' : '#f44336' },
    { label: 'Total Tests', value: totalTests, color: '#1D4434' },
    { label: 'Errors', value: totalErrors, color: totalErrors > 0 ? '#f44336' : '#4caf50' },
    { label: 'Timeouts', value: totalTimeouts, color: totalTimeouts > 0 ? '#ff9800' : '#4caf50' },
  ].map(c => `
    <div style="flex:1;min-width:120px;border:1px solid #e0e0e0;border-radius:8px;padding:14px 18px;background:#fff">
      <div style="font-size:0.7rem;font-weight:600;text-transform:uppercase;letter-spacing:0.06em;color:#757575">${esc(c.label)}</div>
      <div style="font-size:1.6rem;font-weight:700;color:${c.color};margin-top:4px">${esc(c.value)}</div>
    </div>`).join('');

  const tableRows = runs.map((run, i) => {
    const hasErrors = (run.summary?.error ?? 0) + (run.summary?.timeout ?? 0) > 0;
    const bg = i % 2 === 0 ? '#fff' : '#f9f9f9';
    const rowBg = hasErrors ? '#fff8f8' : bg;
    return `
    <tr style="background:${rowBg}">
      <td>${esc(fmtDateTime(run.createdAt))}</td>
      <td>${esc(run.triggeredBy || '—')}</td>
      <td>${badge(run.status)}</td>
      <td style="text-align:center">${esc(run.summary?.total ?? '—')}</td>
      <td style="text-align:center;color:#4caf50;font-weight:600">${esc(run.summary?.success ?? '—')}</td>
      <td style="text-align:center;color:${(run.summary?.error ?? 0) > 0 ? '#f44336' : '#757575'};font-weight:${(run.summary?.error ?? 0) > 0 ? '700' : '400'}">${esc(run.summary?.error ?? '—')}</td>
      <td style="text-align:center;color:${(run.summary?.timeout ?? 0) > 0 ? '#ff9800' : '#757575'};font-weight:${(run.summary?.timeout ?? 0) > 0 ? '700' : '400'}">${esc(run.summary?.timeout ?? '—')}</td>
      <td>${esc(fmtDuration(run.createdAt, run.completedAt))}</td>
    </tr>`;
  }).join('');

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>Synthetic Tests — ${esc(date)}</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 0; padding: 24px 32px; background: #f5f5f5; color: #212121; }
  h1 { color: #1D4434; margin: 0 0 4px; font-size: 1.5rem; }
  .meta { color: #757575; font-size: 0.85rem; margin-bottom: 24px; }
  .cards { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 24px; }
  table { width: 100%; border-collapse: collapse; background: #fff; border-radius: 8px; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
  th { background: #1D4434; color: #fff; padding: 10px 14px; text-align: left; font-size: 0.82rem; font-weight: 600; white-space: nowrap; }
  td { padding: 9px 14px; font-size: 0.83rem; border-bottom: 1px solid #f0f0f0; vertical-align: middle; }
  tr:last-child td { border-bottom: none; }
  footer { margin-top: 20px; font-size: 0.75rem; color: #9e9e9e; }
</style>
</head>
<body>
  <h1>Synthetic Tests — Run History</h1>
  <div class="meta">Exported on ${esc(new Date().toLocaleString())} &nbsp;|&nbsp; Filter: <strong>${esc(filterLabel)}</strong> &nbsp;|&nbsp; ${esc(runs.length)} run${runs.length !== 1 ? 's' : ''}</div>
  <div class="cards">${summaryCards}</div>
  <table>
    <thead>
      <tr>
        <th>Run Time</th><th>Triggered By</th><th>Status</th>
        <th style="text-align:center">Total</th><th style="text-align:center">Success</th>
        <th style="text-align:center">Error</th><th style="text-align:center">Timeout</th>
        <th>Duration</th>
      </tr>
    </thead>
    <tbody>${tableRows}</tbody>
  </table>
  <footer>Generated by HighQ Metrics Portal</footer>
</body>
</html>`;

  const blob = new Blob([html], { type: 'text/html;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `synthetic-tests-${date}.html`;
  a.click();
  URL.revokeObjectURL(url);
};

const STATUS_COLOR = { success: '#4caf50', error: '#f44336', timeout: '#ff9800', running: '#1976d2', completed: '#4caf50', failed: '#f44336' };

const StatusChip = ({ status, size = 'small' }) => {
  const color = STATUS_COLOR[status] || '#607d8b';
  return (
    <Chip
      size={size}
      label={status ?? '—'}
      sx={{ backgroundColor: color, color: '#fff', fontWeight: 600, textTransform: 'capitalize', '& .MuiChip-icon': { color: '#fff' } }}
    />
  );
};

const ENV_COLOR = { PROD: '#d32f2f', UAT: '#f57c00', 'Non-PROD': '#1565c0', Lab: '#555' };
const EnvChip = ({ env }) => (
  <Chip size="small" label={env ?? '—'} sx={{ backgroundColor: ENV_COLOR[env] || '#607d8b', color: '#fff', fontWeight: 500 }} />
);

// ─── output viewer dialog ─────────────────────────────────────────────────────

const OutputDialog = ({ item, onClose }) => {
  if (!item) return null;
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{item.name}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>{item.url}</Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <StatusChip status={item.overallStatus} />
          {item.environment && <EnvChip env={item.environment} />}
          {item.region && <Chip size="small" label={item.region} variant="outlined" />}
          {item.timeTaken != null && <Chip size="small" label={`${item.timeTaken} ms`} variant="outlined" />}
          {item.retried && <Chip size="small" label="Retried" sx={{ backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 600 }} />}
        </Box>

        {/* Error table rows if present */}
        {item.errorRows?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#c62828', display: 'block', mb: 0.5 }}>
              Error Details ({item.errorRows.length} service{item.errorRows.length !== 1 ? 's' : ''} in error)
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#fafafa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Service Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Time Taken (ms)</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {item.errorRows.map((row, i) => (
                  <TableRow key={i} sx={{ backgroundColor: '#ffebee' }}>
                    <TableCell>{row.serviceName || '—'}</TableCell>
                    <TableCell><StatusChip status={row.status?.toLowerCase()} /></TableCell>
                    <TableCell align="right">{row.timeTaken ?? '—'}</TableCell>
                    <TableCell sx={{ color: '#c62828', fontSize: '0.82rem' }}>{row.error || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* All table rows if it was a table response */}
        {item.tableRows?.length > 0 && item.overallStatus === 'success' && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              Service Status ({item.tableRows.length} services)
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#fafafa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Service Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Time Taken (ms)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {item.tableRows.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>{row.serviceName || '—'}</TableCell>
                    <TableCell>{row.status || '—'}</TableCell>
                    <TableCell align="right">{row.timeTaken ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* Raw output */}
        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Raw Output</Typography>
        <Box
          component="pre"
          sx={{
            backgroundColor: '#f5f5f5',
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            p: 1.5,
            overflowX: 'auto',
            fontSize: '0.78rem',
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxHeight: 300,
            overflowY: 'auto',
          }}
        >
          {item.rawOutput || item.errorMessage || '(no output)'}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── URL result row inside an expanded run ────────────────────────────────────

const UrlResultRow = ({ item, onViewOutput }) => {
  const hasErrors = item.overallStatus === 'error' && item.errorRows?.length > 0;
  const [errOpen, setErrOpen] = useState(false);

  return (
    <>
      <TableRow hover sx={{ '& td': { fontSize: '0.82rem' } }}>
        <TableCell sx={{ width: 36, p: 0.5 }}>
          {hasErrors && (
            <IconButton size="small" onClick={() => setErrOpen(v => !v)}>
              {errOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
        <TableCell sx={{ fontWeight: 500, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.name}
        </TableCell>
        <TableCell>{item.region || '—'}</TableCell>
        <TableCell><EnvChip env={item.environment} /></TableCell>
        <TableCell>
          <StatusChip status={item.overallStatus} />
          {item.retried && <Chip size="small" label="Retried" sx={{ ml: 0.5, backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 600 }} />}
        </TableCell>
        <TableCell align="right">{item.timeTaken != null ? `${item.timeTaken} ms` : '—'}</TableCell>
        <TableCell align="right">
          <Tooltip title="View Output">
            <IconButton size="small" onClick={() => onViewOutput(item)} sx={{ color: darkGreen }}>
              <CodeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>

      {/* Inline error message for non-table errors */}
      {item.overallStatus === 'error' && item.errorRows?.length === 0 && item.errorMessage && (
        <TableRow>
          <TableCell colSpan={7} sx={{ py: 0 }}>
            <Box sx={{ pl: 7, py: 0.75, color: '#c62828', fontSize: '0.78rem' }}>{item.errorMessage.slice(0, 200)}</Box>
          </TableCell>
        </TableRow>
      )}

      {/* Expandable error sub-table */}
      {hasErrors && (
        <TableRow>
          <TableCell colSpan={7} sx={{ p: 0, borderBottom: errOpen ? undefined : 'none' }}>
            <Collapse in={errOpen} timeout="auto" unmountOnExit>
              <Box sx={{ mx: 7, my: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#fafafa' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Service Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Time (ms)</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {item.errorRows.map((row, i) => (
                      <TableRow key={i} sx={{ backgroundColor: '#ffebee' }}>
                        <TableCell>{row.serviceName || '—'}</TableCell>
                        <TableCell><Chip size="small" label={row.status || '—'} sx={{ backgroundColor: '#f44336', color: '#fff', fontWeight: 600 }} /></TableCell>
                        <TableCell align="right">{row.timeTaken ?? '—'}</TableCell>
                        <TableCell sx={{ color: '#c62828', fontSize: '0.78rem' }}>{row.error || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

// ─── expandable run row ───────────────────────────────────────────────────────

const RunRow = ({ run, onViewOutput, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isRunning = run.status === 'running';
  const hasErrors = (run.summary?.error ?? 0) + (run.summary?.timeout ?? 0) > 0;

  const handleToggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !detail && !isRunning) {
      setLoadingDetail(true);
      try {
        const resp = await authGet(`/api/synthetic-tests/runs/${run._id}`, null, 'json', { useCache: true, cacheTTL: 300 });
        setDetail(resp?.data ?? null);
      } catch (err) {
        console.error('RunRow: failed to fetch run detail', err);
      } finally {
        setLoadingDetail(false);
      }
    }
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this run record?')) return;
    setDeleting(true);
    try {
      await authDelete(`/api/synthetic-tests/runs/${run._id}`);
      onDelete(run._id);
    } catch {
      setDeleting(false);
    }
  };

  const results = detail?.results ?? [];

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: 'pointer', backgroundColor: hasErrors ? '#fff8f8' : undefined }}
        onClick={handleToggle}
      >
        <TableCell sx={{ width: 36, p: 0.5 }}>
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </TableCell>
        <TableCell sx={{ fontSize: '0.83rem' }}>{fmtDateTime(run.createdAt)}</TableCell>
        <TableCell sx={{ fontSize: '0.83rem' }}>{run.triggeredBy || '—'}</TableCell>
        <TableCell><StatusChip status={run.status} /></TableCell>
        <TableCell align="center">{run.summary?.total ?? '—'}</TableCell>
        <TableCell align="center">
          {run.summary?.success != null && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 14, color: STATUS_COLOR.success }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: STATUS_COLOR.success }}>{run.summary.success}</Typography>
            </Box>
          )}
        </TableCell>
        <TableCell align="center">
          {(run.summary?.error ?? 0) > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
              <ErrorIcon sx={{ fontSize: 14, color: STATUS_COLOR.error }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: STATUS_COLOR.error }}>{run.summary.error}</Typography>
            </Box>
          )}
        </TableCell>
        <TableCell align="center">
          {(run.summary?.timeout ?? 0) > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
              <AccessTimeIcon sx={{ fontSize: 14, color: STATUS_COLOR.timeout }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: STATUS_COLOR.timeout }}>{run.summary.timeout}</Typography>
            </Box>
          )}
        </TableCell>
        <TableCell sx={{ fontSize: '0.83rem' }}>{fmtDuration(run.createdAt, run.completedAt)}</TableCell>
        <TableCell align="right" onClick={e => e.stopPropagation()}>
          <Tooltip title="Delete run">
            <span>
              <IconButton size="small" onClick={handleDelete} disabled={deleting} sx={{ color: 'error.main' }}>
                {deleting ? <CircularProgress size={14} /> : <DeleteIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={9} sx={{ p: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ mx: 2, my: 1 }}>
              {isRunning && <LinearProgress sx={{ mb: 1 }} />}
              {loadingDetail ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : results.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1, pl: 2 }}>
                  {isRunning ? 'Run in progress…' : 'No results stored.'}
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ width: 36 }} />
                      <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Region</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Environment</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Time Taken</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Output</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.map((item, idx) => (
                      <UrlResultRow key={idx} item={item} onViewOutput={onViewOutput} />
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ─── most failed dialog ───────────────────────────────────────────────────────

const MostFailedDialog = ({ mostFailed, onClose }) => {
  if (!mostFailed) return null;
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#c62828' }}>
            Most Failed URL
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Failed in <strong>{mostFailed.count}</strong> run{mostFailed.count !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#fff3f3', border: '1px solid #ffcdd2', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#c62828', wordBreak: 'break-all' }}>
            {mostFailed.name}
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>
          Runs with failures
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#fafafa' }}>
              <TableCell sx={{ fontWeight: 600 }}>Run Time</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Triggered By</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Errors</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Timeouts</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mostFailed.runs.map((run, i) => (
              <TableRow key={i} sx={{ backgroundColor: '#fff8f8' }}>
                <TableCell sx={{ fontSize: '0.82rem' }}>{fmtDateTime(run.createdAt)}</TableCell>
                <TableCell sx={{ fontSize: '0.82rem' }}>{run.triggeredBy || '—'}</TableCell>
                <TableCell align="center">
                  {(run.summary?.error ?? 0) > 0
                    ? <Typography variant="caption" sx={{ fontWeight: 700, color: '#f44336' }}>{run.summary.error}</Typography>
                    : <Typography variant="caption" color="text.disabled">—</Typography>}
                </TableCell>
                <TableCell align="center">
                  {(run.summary?.timeout ?? 0) > 0
                    ? <Typography variant="caption" sx={{ fontWeight: 700, color: '#ff9800' }}>{run.summary.timeout}</Typography>
                    : <Typography variant="caption" color="text.disabled">—</Typography>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── metric card ──────────────────────────────────────────────────────────────

const MetricCard = ({ label, value, sub, color, onClick }) => (
  <Card
    elevation={0}
    onClick={onClick}
    sx={{
      flex: 1, minWidth: 140, border: '1px solid #e0e0e0', borderRadius: 2,
      cursor: onClick ? 'pointer' : 'default',
      transition: 'box-shadow 0.15s, border-color 0.15s',
      '&:hover': onClick ? { boxShadow: 3, borderColor: '#bdbdbd' } : {},
    }}
  >
    <CardContent sx={{ pb: '12px !important' }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, color: color || darkGreen, mt: 0.5 }}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </CardContent>
  </Card>
);

// ─── main component ───────────────────────────────────────────────────────────

const ENVS = ['PROD', 'UAT', 'Non-PROD', 'Lab'];
const TIME_OPTIONS = [
  { value: '1d', label: 'Last 24h' },
  { value: '7d', label: 'Last 7d' },
  { value: '30d', label: 'Last 30d' },
  { value: 'all', label: 'All Time' },
];

const SyntheticTestsTab = () => {
  const [runs, setRuns]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [runAlert, setRunAlert]     = useState(null);
  const [triggering, setTriggering] = useState(false);
  const [outputItem, setOutputItem]           = useState(null);
  const [mostFailedOpen, setMostFailedOpen]   = useState(false);
  const [scheduler, setScheduler]             = useState(null);

  // Filters
  const [filterTime, setFilterTime]     = useState('7d');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterEnv, setFilterEnv]       = useState('all');

  const pollRef = useRef(null);

  // ── fetch ──────────────────────────────────────────────────────────────────

  const fetchRuns = async ({ fresh = false } = {}) => {
    try {
      const resp = await authGet('/api/synthetic-tests/runs', { limit: 100 }, 'json', {
        useCache: !fresh,
        cacheTTL: 60,
      });
      setRuns(resp?.data ?? []);
    } catch (err) {
      console.error('SyntheticTestsTab: error fetching runs', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchScheduler = async () => {
    try {
      const resp = await authGet('/api/synthetic-tests/scheduler', null, 'json', { useCache: false });
      setScheduler(resp?.data ?? null);
    } catch {
      // scheduler info is non-critical
    }
  };

  // ── polling ────────────────────────────────────────────────────────────────

  const isAnyRunning = (runList) => runList.some(r => r.status === 'running');

  const startPolling = () => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const resp = await authGet('/api/synthetic-tests/runs', { limit: 100 }, 'json', { useCache: false, cacheTTL: 0 }).catch(() => null);
      const list = resp?.data ?? [];
      setRuns(list);
      if (!isAnyRunning(list)) stopPolling();
    }, 5000);
  };

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  useEffect(() => {
    fetchRuns().then(() => {
      if (isAnyRunning(runs)) startPolling();
    });
    fetchScheduler();
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── trigger run ────────────────────────────────────────────────────────────

  const handleRunNow = async () => {
    setTriggering(true);
    setRunAlert(null);
    try {
      await authPost('/api/synthetic-tests/run', {});
      setRunAlert({ severity: 'info', message: 'Test run started — results will appear once complete.' });
      await fetchRuns({ fresh: true });
      startPolling();
    } catch (err) {
      if (err?.status === 409 || err?.response?.status === 409) {
        const body = err?.data ?? err?.response?.data ?? {};
        setRunAlert({ severity: 'warning', message: `Another run was already triggered by ${body.runningBy ?? 'someone'} at ${body.runningAt ? fmtDateTime(body.runningAt) : 'recently'}` });
      } else {
        setRunAlert({ severity: 'error', message: 'Failed to trigger a new run. Please try again.' });
      }
    } finally {
      setTriggering(false);
    }
  };

  // ── filtering ──────────────────────────────────────────────────────────────

  const filteredRuns = useMemo(() => {
    const cutoff = (() => {
      if (filterTime === 'all') return null;
      const d = new Date();
      if (filterTime === '1d') d.setDate(d.getDate() - 1);
      if (filterTime === '7d') d.setDate(d.getDate() - 7);
      if (filterTime === '30d') d.setDate(d.getDate() - 30);
      return d;
    })();

    return runs.filter(run => {
      if (cutoff && new Date(run.createdAt) < cutoff) return false;
      if (filterStatus === 'errors' && (run.summary?.error ?? 0) + (run.summary?.timeout ?? 0) === 0) return false;
      if (filterStatus === 'ok' && ((run.summary?.error ?? 0) + (run.summary?.timeout ?? 0)) > 0) return false;
      if (filterStatus === 'running' && run.status !== 'running') return false;
      // env filter omitted — results are lazy-loaded and not available in summaries
      return true;
    });
  }, [runs, filterTime, filterStatus, filterEnv]);

  // ── metrics ────────────────────────────────────────────────────────────────

  const metrics = useMemo(() => {
    if (filteredRuns.length === 0) return null;
    const completed = filteredRuns.filter(r => r.status === 'completed');
    const totalTests = completed.reduce((s, r) => s + (r.summary?.total ?? 0), 0);
    const totalSuccess = completed.reduce((s, r) => s + (r.summary?.success ?? 0), 0);
    const successRate = totalTests === 0 ? null : Math.round((totalSuccess / totalTests) * 100);

    // Most failed URL across filtered runs — uses denormalized failedNames from summary
    const failCounts = {};
    filteredRuns.forEach(run => {
      (run.summary?.failedNames ?? []).forEach(name => {
        failCounts[name] = (failCounts[name] || 0) + 1;
      });
    });
    const mostFailedEntry = Object.entries(failCounts).sort((a, b) => b[1] - a[1])[0];
    const mostFailed = mostFailedEntry
      ? {
          name: mostFailedEntry[0],
          count: mostFailedEntry[1],
          runs: filteredRuns.filter(r => r.summary?.failedNames?.includes(mostFailedEntry[0])),
        }
      : null;

    const latest = filteredRuns[0];
    return { successRate, mostFailed, latest, totalRuns: filteredRuns.length };
  }, [filteredRuns]);

  const anyRunning = isAnyRunning(runs);

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" sx={{ color: darkGreen, fontWeight: 700 }}>Synthetic Tests</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={() => fetchRuns({ fresh: true })} size="small" disabled={anyRunning}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="View test documentation">
            <Button
              variant="outlined"
              startIcon={<ArticleIcon />}
              component="a"
              href="/docs/checkSystemHealth-overview.html"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              What's Tested
            </Button>
          </Tooltip>
          <Button
            variant="contained"
            color="success"
            startIcon={triggering ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <PlayArrowIcon />}
            onClick={handleRunNow}
            disabled={triggering || anyRunning}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Run Now
          </Button>
        </Box>
      </Box>

      {/* ── Schedule info bar ── */}
      {scheduler && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            {scheduler.isEnabled ? (
              <>
                <Typography variant="caption" color="text.secondary">
                  {scheduler.mode === 'interval'
                    ? `Auto-run every ${scheduler.intervalHours}h`
                    : `Auto-run daily at ${String(scheduler.specificTime?.hour ?? 9).padStart(2, '0')}:${String(scheduler.specificTime?.minute ?? 0).padStart(2, '0')} (${scheduler.timezone})`}
                </Typography>
                {scheduler.nextRun && (
                  <Chip
                    size="small"
                    label={`Next: ${fmtDateTime(scheduler.nextRun)}`}
                    sx={{ fontSize: '0.72rem', height: 20, backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }}
                  />
                )}
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">Auto-schedule disabled</Typography>
            )}
          </Box>
          {scheduler.lastRun && (
            <Typography variant="caption" color="text.secondary">
              Last run: {fmtDateTime(scheduler.lastRun)}
            </Typography>
          )}
        </Box>
      )}

      {/* ── Alert ── */}
      {runAlert && <Alert severity={runAlert.severity} onClose={() => setRunAlert(null)}>{runAlert.message}</Alert>}

      {/* ── Metric cards ── */}
      {metrics && (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <MetricCard label="Total Runs" value={metrics.totalRuns} sub={filterTime !== 'all' ? TIME_OPTIONS.find(t => t.value === filterTime)?.label : 'All time'} />
          <MetricCard
            label="Success Rate"
            value={metrics.successRate != null ? `${metrics.successRate}%` : '—'}
            sub="across completed runs"
            color={metrics.successRate != null ? (metrics.successRate >= 90 ? '#4caf50' : metrics.successRate >= 70 ? '#ff9800' : '#f44336') : undefined}
          />
          <MetricCard
            label="Last Run"
            value={<StatusChip status={metrics.latest?.status} size="medium" />}
            sub={fmtDateTime(metrics.latest?.createdAt)}
          />
          {metrics.mostFailed && (
            <MetricCard
              label="Most Failed"
              value={metrics.mostFailed.count}
              sub={`${metrics.mostFailed.name} — click for details`}
              color="#f44336"
              onClick={() => setMostFailedOpen(true)}
            />
          )}
        </Box>
      )}

      {/* ── Filters ── */}
      <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>

          {/* Time range */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Time Range</Typography>
            <ToggleButtonGroup
              value={filterTime}
              exclusive
              onChange={(_, v) => v && setFilterTime(v)}
              size="small"
            >
              {TIME_OPTIONS.map(o => (
                <ToggleButton key={o.value} value={o.value} sx={{ textTransform: 'none', fontSize: '0.78rem', px: 1.5 }}>
                  {o.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {/* Status */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Status</Typography>
            <ToggleButtonGroup
              value={filterStatus}
              exclusive
              onChange={(_, v) => v && setFilterStatus(v)}
              size="small"
            >
              {[['all', 'All'], ['ok', 'Success Only'], ['errors', 'Has Errors'], ['running', 'Running']].map(([v, l]) => (
                <ToggleButton key={v} value={v} sx={{ textTransform: 'none', fontSize: '0.78rem', px: 1.5 }}>{l}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {/* Environment */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Environment</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {['all', ...ENVS].map(env => (
                <Chip
                  key={env}
                  label={env === 'all' ? 'All' : env}
                  size="small"
                  clickable
                  onClick={() => setFilterEnv(env)}
                  sx={{
                    fontWeight: filterEnv === env ? 700 : 400,
                    backgroundColor: filterEnv === env ? (ENV_COLOR[env] || darkGreen) : '#f0f0f0',
                    color: filterEnv === env ? '#fff' : 'inherit',
                    border: filterEnv === env ? 'none' : '1px solid #ddd',
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* ── Runs history table ── */}
      <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: darkGreen }}>Run History</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              {filteredRuns.length} run{filteredRuns.length !== 1 ? 's' : ''}
              {filteredRuns.length !== runs.length ? ` (filtered from ${runs.length})` : ''}
            </Typography>
            {filteredRuns.length > 0 && (
              <Tooltip title="Export as HTML report">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon fontSize="small" />}
                  onClick={() => exportRunsHtml(
                    filteredRuns,
                    metrics,
                    TIME_OPTIONS.find(t => t.value === filterTime)?.label ?? filterTime,
                  )}
                  sx={{ textTransform: 'none', fontSize: '0.78rem', py: 0.25 }}
                >
                  Export HTML
                </Button>
              </Tooltip>
            )}
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}><CircularProgress size={32} /></Box>
        ) : filteredRuns.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            {runs.length === 0 ? 'No runs yet. Click "Run Now" to start the first run.' : 'No runs match the current filters.'}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ width: 36 }} />
                  <TableCell sx={{ fontWeight: 600 }}>Run Time</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Triggered By</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Total</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Success</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Error</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Timeout</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRuns.map((run, idx) => (
                  <RunRow
                    key={run._id ?? idx}
                    run={run}
                    onViewOutput={setOutputItem}
                    onDelete={(id) => setRuns(prev => prev.filter(r => r._id !== id))}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ── Output viewer dialog ── */}
      {outputItem && <OutputDialog item={outputItem} onClose={() => setOutputItem(null)} />}

      {/* ── Most failed dialog ── */}
      {mostFailedOpen && metrics?.mostFailed && (
        <MostFailedDialog mostFailed={metrics.mostFailed} onClose={() => setMostFailedOpen(false)} />
      )}
    </Box>
  );
};

export default SyntheticTestsTab;
